import { useState, useEffect, FormEvent } from "react";
import AdminLayout from "../components/AdminLayout";
import { supabase } from "../../../lib/supabase";

type ProductColor = {
  color: string;
  images: string[];
};

type ProductForm = {
  name: string;
  sku: string;
  size: string;
  material: string;
  description: string;
  short_description: string;
  image_url: string;
  featured: boolean;
  is_new: boolean;
  slug: string;
  colors: ProductColor[];
  visible: boolean;
  categories: string[];
};

type AdminProduct = {
  id: string;
  name: string;
  sku: string;
  size: string;
  sizes?: string;
  material: string;
  description: string;
  short_description: string;
  image_url: string;
  featured: boolean;
  is_new: boolean;
  slug: string;
  colors: ProductColor[];
  visible: boolean;
  categories: string[];
  created_at?: string;
  source: "shared" | "exclusive";
  store_product_id?: string | null;
  product_id?: string | null;
};

const STORE_SLUG = "ray_melo";

const emptyForm: ProductForm = {
  name: "",
  sku: "",
  size: "",
  material: "",
  description: "",
  short_description: "",
  image_url: "",
  featured: false,
  is_new: false,
  slug: "",
  colors: [],
  visible: true,
  categories: [],
};

const normalizeColors = (colors: any): ProductColor[] => {
  if (!Array.isArray(colors)) return [];

  return colors.map((item: any) => {
    if (Array.isArray(item?.images)) {
      return {
        color: item?.color || item?.name || "",
        images: item.images.filter(
          (img: any) => typeof img === "string" && img.trim()
        ),
      };
    }

    if (typeof item?.image === "string" && item.image.trim()) {
      return {
        color: item?.color || item?.name || "",
        images: [item.image],
      };
    }

    return {
      color: item?.color || item?.name || "",
      images: [],
    };
  });
};

export default function AdminProducts() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStoreProductId, setEditingStoreProductId] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState<"shared" | "exclusive" | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>("editor");

  const generateSlug = (name: string) =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const fetchUserRole = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setUserRole("editor");
      return;
    }

    const { data, error } = await supabase
      .from("admin_users")
      .select("role")
      .eq("id", user.id)
      .single();

    if (error || !data?.role) {
      setUserRole("editor");
      return;
    }

    setUserRole(data.role);
  };

  const fetchStoreId = async () => {
    const { data, error } = await supabase
      .from("stores")
      .select("id")
      .eq("slug", STORE_SLUG)
      .single();

    if (error || !data) {
      console.error(error);
      return null;
    }

    setStoreId(data.id);
    return data.id as string;
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data: exclusiveData, error: exclusiveError } = await supabase
        .from("ray_melo_products")
        .select("*")
        .order("created_at", { ascending: false });

      if (exclusiveError) {
        console.error(exclusiveError);
        alert("Erro ao buscar produtos");
        return;
      }

      const exclusiveProducts: AdminProduct[] =
        exclusiveData?.map((item: any) => ({
          id: item.id,
          name: item.name || "",
          sku: item.sku || "",
          size: item.size || item.sizes || "",
          sizes: item.sizes || item.size || "",
          material: item.material || "",
          description: item.description || "",
          short_description: item.short_description || "",
          image_url: item.image_url || "",
          featured: !!item.featured,
          is_new: !!item.is_new,
          slug: item.slug || "",
          colors: normalizeColors(item.colors),
          visible: item.visible !== false,
          categories: Array.isArray(item.categories) ? item.categories : [],
          created_at: item.created_at,
          source: "exclusive",
          store_product_id: null,
          product_id: null,
        })) || [];

      setProducts(exclusiveProducts);
    } catch (err) {
      console.error("Erro inesperado:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    const { data, error } = await supabase.from("categories").select("*");

    if (error) {
      console.error(error);
      return;
    }

    setAllCategories(data || []);
  };

  useEffect(() => {
    const init = async () => {
      await fetchUserRole();
      await fetchStoreId();
      await fetchProducts();
      await fetchCategories();
    };

    init();
  }, []);

  const openNewForm = () => {
    setEditingId(null);
    setEditingStoreProductId(null);
    setEditingSource("exclusive");
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (product: AdminProduct) => {
    setEditingId(product.id);
    setEditingStoreProductId(null);
    setEditingSource("exclusive");
    setForm({
      name: product.name || "",
      sku: product.sku || "",
      size: product.size || product.sizes || "",
      material: product.material || "",
      description: product.description || "",
      short_description: product.short_description || "",
      image_url: product.image_url || "",
      featured: !!product.featured,
      is_new: !!product.is_new,
      slug: product.slug || "",
      colors: normalizeColors(product.colors),
      visible: product.visible !== false,
      categories: Array.isArray(product.categories) ? product.categories : [],
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingStoreProductId(null);
    setEditingSource(null);
    setForm(emptyForm);
  };

  const addColor = () => {
    setForm((prev) => ({
      ...prev,
      colors: [...prev.colors, { color: "", images: [] }],
    }));
  };

  const removeColor = (index: number) => {
    setForm((prev) => ({
      ...prev,
      colors: prev.colors.filter((_, i) => i !== index),
    }));
  };

  const updateColorName = (index: number, value: string) => {
    setForm((prev) => {
      const updated = [...prev.colors];
      updated[index] = {
        ...updated[index],
        color: value,
      };
      return { ...prev, colors: updated };
    });
  };

  const addColorImageByUrl = (index: number, value: string) => {
    const url = value.trim();
    if (!url) return;

    setForm((prev) => {
      const updated = [...prev.colors];
      updated[index] = {
        ...updated[index],
        images: [...updated[index].images, url],
      };
      return { ...prev, colors: updated };
    });
  };

  const removeColorImage = (colorIndex: number, imageIndex: number) => {
    setForm((prev) => {
      const updated = [...prev.colors];
      updated[colorIndex] = {
        ...updated[colorIndex],
        images: updated[colorIndex].images.filter((_, i) => i !== imageIndex),
      };
      return { ...prev, colors: updated };
    });
  };

  const handleColorUpload = async (index: number, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const uploadedUrls: string[] = [];

    for (const file of Array.from(files)) {
      const safeName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9.-]/g, "");

      const fileName = `products/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("bozzetti-assets")
        .upload(fileName, file);

      if (uploadError) {
        console.error("Erro no upload:", uploadError);
        alert(`Erro no upload: ${uploadError.message}`);
        return;
      }

      const { data } = supabase.storage
        .from("bozzetti-assets")
        .getPublicUrl(fileName);

      uploadedUrls.push(data.publicUrl);
    }

    setForm((prev) => {
      const updated = [...prev.colors];
      updated[index] = {
        ...updated[index],
        images: [...updated[index].images, ...uploadedUrls],
      };
      return { ...prev, colors: updated };
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: form.name,
        sku: form.sku,
        size: form.size,
        sizes: form.size,
        material: form.material,
        description: form.description,
        short_description: form.short_description,
        image_url: form.image_url,
        featured: form.featured,
        is_new: form.is_new,
        slug: form.slug || generateSlug(form.name),
        colors: form.colors,
        visible: form.visible,
        categories: form.categories,
      };

      if (editingId) {
        const { error } = await supabase
          .from("ray_melo_products")
          .update(payload)
          .eq("id", editingId);

        if (error) {
          console.error(error);
          alert("Erro ao atualizar produto: " + error.message);
          return;
        }
        alert("Produto atualizado com sucesso");
      } else {
        const { error } = await supabase
          .from("ray_melo_products")
          .insert([payload]);

        if (error) {
          console.error(error);
          alert("Erro ao salvar produto: " + error.message);
          return;
        }
        alert("Produto salvo com sucesso");
      }

      closeForm();
      fetchProducts();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (product: AdminProduct) => {
    if (userRole !== "admin") {
      alert("Você não tem permissão para excluir produtos.");
      return;
    }

    const confirmed = window.confirm("Tem certeza que deseja excluir este produto?");
    if (!confirmed) return;

    const { error } = await supabase
      .from("ray_melo_products")
      .delete()
      .eq("id", product.id);

    if (error) {
      console.error(error);
      alert("Erro ao excluir produto: " + error.message);
      return;
    }

    alert("Produto removido");
    fetchProducts();
  };

  const toggleVisibility = async (product: AdminProduct) => {
    const newVisible = product.visible === false ? true : false;

    const { error } = await supabase
      .from("ray_melo_products")
      .update({ visible: newVisible })
      .eq("id", product.id);

    if (error) {
      console.error(error);
      alert("Erro ao alterar visibilidade");
      return;
    }

    fetchProducts();
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-semibold">Produtos</h1>

          <button
            onClick={openNewForm}
            className="bg-black text-white px-4 py-2 rounded"
          >
            + Novo Produto
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {products.map((p) => (
            <div
              key={p.id}
              className="border p-3 rounded bg-white"
            >
              {p.image_url && (
                <img
                  src={p.image_url}
                  className="w-full h-40 object-cover mb-3 rounded"
                  alt={p.name}
                />
              )}

              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="font-medium">{p.name}</p>

                {p.visible === false && (
                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                    Oculto
                  </span>
                )}
              </div>

              <div className="flex gap-2 flex-wrap mb-3">
                {p.featured && (
                  <span className="inline-block text-xs bg-black text-white px-2 py-1 rounded">
                    Destaque
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={() => openEditForm(p)}
                  className="border px-3 py-1 rounded text-sm"
                >
                  Editar
                </button>

                <button
                  onClick={() => toggleVisibility(p)}
                  className="border px-3 py-1 rounded text-sm"
                >
                  {p.visible === false ? "Mostrar" : "Ocultar"}
                </button>

                {userRole === "admin" && (
                  <button
                    onClick={() => handleDelete(p)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex justify-center items-start p-4 overflow-auto z-50">
            <form
              onSubmit={handleSubmit}
              className="bg-white w-full max-w-3xl p-6 rounded space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {editingId ? "Editar Produto" : "Novo Produto"}
                </h2>

                <button
                  type="button"
                  onClick={closeForm}
                  className="border px-3 py-1 rounded"
                >
                  Fechar
                </button>
              </div>

              <input
                value={form.name}
                placeholder="Nome"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                    slug: generateSlug(e.target.value),
                  }))
                }
              />

              <input
                value={form.slug}
                placeholder="Slug"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, slug: e.target.value }))
                }
              />

              <input
                value={form.sku}
                placeholder="SKU"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, sku: e.target.value }))
                }
              />

              <input
                value={form.size}
                placeholder="Tamanhos (ex: PP,P,M,G)"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, size: e.target.value }))
                }
              />

              <input
                value={form.material}
                placeholder="Material"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, material: e.target.value }))
                }
              />

              <textarea
                value={form.description}
                placeholder="Descrição completa"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />

              <input
                value={form.short_description}
                placeholder="Descrição curta"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    short_description: e.target.value,
                  }))
                }
              />

              <input
                value={form.image_url}
                placeholder="Imagem principal URL"
                className="w-full border p-2"
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, image_url: e.target.value }))
                }
              />

              <div>
                <p className="font-medium mb-2">Categorias</p>
                <div className="flex flex-wrap gap-2">
                  {allCategories.map((cat) => {
                    const active = form.categories.includes(cat.slug);
                    return (
                      <button
                        type="button"
                        key={cat.id}
                        onClick={() => {
                          setForm((prev) => ({
                            ...prev,
                            categories: active
                              ? prev.categories.filter((c) => c !== cat.slug)
                              : [...prev.categories, cat.slug],
                          }));
                        }}
                        className={`px-3 py-1 border rounded text-sm ${
                          active ? "bg-black text-white" : "bg-white text-black"
                        }`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Cores</span>
                  <button
                    type="button"
                    onClick={addColor}
                    className="border px-3 py-1 rounded"
                  >
                    + adicionar
                  </button>
                </div>

                {form.colors.map((c, i) => (
                  <div key={i} className="border rounded p-3 mb-3 space-y-3">
                    <input
                      value={c.color}
                      placeholder="Cor"
                      className="w-full border p-2"
                      onChange={(e) => updateColorName(i, e.target.value)}
                    />
                    <div className="flex gap-2">
                      <input
                        id={`color-image-url-${i}`}
                        placeholder="URL da imagem"
                        className="w-full border p-2"
                      />
                      <button
                        type="button"
                        className="border px-3 rounded whitespace-nowrap"
                        onClick={() => {
                          const input = document.getElementById(
                            `color-image-url-${i}`
                          ) as HTMLInputElement | null;
                          if (!input) return;
                          addColorImageByUrl(i, input.value);
                          input.value = "";
                        }}
                      >
                        Adicionar URL
                      </button>
                    </div>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleColorUpload(i, e.target.files)}
                    />
                    {c.images.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {c.images.map((img, imageIndex) => (
                          <div
                            key={`${img}-${imageIndex}`}
                            className="border rounded p-2"
                          >
                            <img
                              src={img}
                              className="w-full h-28 object-cover rounded mb-2"
                              alt={c.color}
                            />
                            <button
                              type="button"
                              onClick={() => removeColorImage(i, imageIndex)}
                              className="w-full bg-red-600 text-white px-2 py-1 rounded text-sm"
                            >
                              Remover foto
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeColor(i)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Remover cor
                    </button>
                  </div>
                ))}
              </div>

              <label className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      featured: e.target.checked,
                    }))
                  }
                />
                Destaque
              </label>

              <label className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={form.is_new}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_new: e.target.checked,
                    }))
                  }
                />
                Novo
              </label>

              <label className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={form.visible}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      visible: e.target.checked,
                    }))
                  }
                />
                Visível
              </label>

              <button
                disabled={loading}
                className="bg-black text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {loading ? "Salvando..." : editingId ? "Atualizar" : "Salvar"}
              </button>
            </form>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}